import type { ComponentType, ReactNode } from 'react';
import type { z } from 'zod';

import type { AppFormValues } from '@/features/dashboard/components/AppForm/AppForm/context/types';
import type { DrawerStep } from '@/features/dashboard/components/Drawer/types';
import type { AppType, MarketApp, TilesWidgetV2, WidgetV2 } from '@/features/dashboard/types';

export type SelectorMode = 'spot' | 'fwd' | 'default';

export enum Category {
  All = 'All',
  Markets = 'Markets',
  Charts = 'Charts',
  Data = 'Data'
}

export interface AppCatalogEntry {
  /** Stable DOM/tracking id, e.g. 'spot_history_table' */
  id: string;
  /** Drawer navigation step */
  step: DrawerStep;
  title: string;
  description: string;
  icon: ReactNode;
  category: Category;
  premium?: boolean;
  soon?: boolean;
  badge?: string;
  /** Selector modes in which this app is NOT available */
  incompatibleWith: SelectorMode[];
}

export type FormAppType =
  | AppType.Spot
  | AppType.Seasonal
  | AppType.FWD
  | AppType.Table
  | AppType.Tiles
  | AppType.SpotHistoryTable;

export type FormAppWidget = WidgetV2 | TilesWidgetV2;

export interface FormAppDefinition<
  TValues extends AppFormValues = AppFormValues,
  TWidget extends FormAppWidget = FormAppWidget
> {
  /** Domain enum value */
  type: FormAppType;

  /** Drawer step to navigate to when editing this app type */
  drawerStep: DrawerStep;

  dashboardControls?: {
    edit?: boolean;
  };

  widgetControls?: {
    export?: boolean;
  };

  maxItems: number;
  initialValues: TValues;

  /** Pinned in-content header shown above the scrolling form body. */
  heading: { title: string; subtitle: string };

  /** Runtime validation for the form branch */
  schema: z.ZodType<TValues>;

  /** Renders the form body inside AppForm */
  Form: ComponentType;

  /** Converts form values to a domain widget for create/update. */
  toWidget: (values: TValues) => MarketApp;

  /** Narrows domain widgets that this definition can edit. */
  canEditWidget: (widget: FormAppWidget) => widget is TWidget;

  /** Converts a domain widget back to form values for edit mode. */
  fromWidget: (widget: TWidget) => TValues;
}
